import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RadicalTrashIcon from "./RadicalTrashIcon";
import {
  RESIDENT_PRESET_TEXT,
  RESIDENT_PASTE_PLACEHOLDER,
  insertPresetText,
  splitHighlightSegments
} from "../../../utils/templateInstructions";

// Shared box metrics so the highlight layer lines up exactly with the textarea text.
const BOX_CLASS = "px-3 py-2 text-sm leading-6 border rounded-md";
const TEXT_STYLE = {
  whiteSpace: "pre-wrap",
  overflowWrap: "break-word",
  wordBreak: "break-word"
};

/**
 * Template Instructions (CRE): predefined "Resident" text, clear button, and a
 * green highlight on the "[הדבק כאן את תשובת המתמחה]" paste placeholder.
 *
 * A textarea cannot colour part of its text, so a mirror layer with identical
 * metrics sits behind a transparent-background textarea and draws the highlight.
 */
export default function TemplateInstructionsField({
  value,
  onChange,
  isRtl,
  showResidentPreset = false
}) {
  const { t } = useTranslation("common");
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const text = value || "";
  const dir = isRtl ? "rtl" : "ltr";
  const hasHighlight = text.includes(RESIDENT_PASTE_PLACEHOLDER);

  // Keep the mirror's wrap width equal to the textarea's (scrollbar steals width).
  const syncLayout = useCallback(() => {
    const ta = textareaRef.current;
    const bd = backdropRef.current;
    if (!ta || !bd) return;
    const cs = window.getComputedStyle(ta);
    const borders =
      (parseFloat(cs.borderLeftWidth) || 0) +
      (parseFloat(cs.borderRightWidth) || 0);
    setScrollbarWidth(Math.max(0, ta.offsetWidth - ta.clientWidth - borders));
    bd.scrollTop = ta.scrollTop;
  }, []);

  useLayoutEffect(() => {
    syncLayout();
  }, [text, syncLayout]);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => syncLayout());
    ro.observe(ta);
    return () => ro.disconnect();
  }, [syncLayout]);

  // Always appended after the existing content (never replaces it).
  const handleInsertResident = () => {
    const result = insertPresetText(
      text,
      text.length,
      text.length,
      RESIDENT_PRESET_TEXT
    );
    onChange(result.value);
    // Select the placeholder so pasting replaces it directly.
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(result.placeholderStart, result.placeholderEnd);
    });
  };

  const handleClear = () => {
    onChange("");
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const scrollbarPad = isRtl
    ? { paddingLeft: `calc(0.75rem + ${scrollbarWidth}px)` }
    : { paddingRight: `calc(0.75rem + ${scrollbarWidth}px)` };

  return (
    <div>
      <div
        dir={dir}
        className="mb-2 flex flex-wrap items-center justify-between gap-2"
      >
        <label
          htmlFor="template-instructions-field"
          className="text-sm font-medium text-gray-900 dark:text-gray-100"
        >
          {t("templateInstructions")}
        </label>
        <div className="flex items-center gap-2">
          {showResidentPreset ? (
            <button
              type="button"
              onClick={handleInsertResident}
              className="rounded-full bg-[#CBEA7B80] px-3 py-1 text-xs font-semibold text-gray-800 transition-colors hover:bg-[#A4C639] dark:bg-[#A4C63980] dark:text-gray-900 dark:hover:bg-[#CBEA7B]"
              title={t("templateInstructionsResidentPresetTitle")}
            >
              {t("templateInstructionsResidentPreset")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleClear}
            disabled={!text}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            title={t("clearTemplateInstructions")}
            aria-label={t("clearTemplateInstructions")}
          >
            <RadicalTrashIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-500" />
          </button>
        </div>
      </div>

      <div className="relative rounded-md bg-white shadow-sm dark:bg-gray-700">
        {/* Highlight layer (behind the textarea). Text is invisible; only marks show. */}
        <div
          ref={backdropRef}
          aria-hidden="true"
          dir={dir}
          className={`${BOX_CLASS} pointer-events-none absolute inset-0 overflow-hidden border-transparent text-transparent`}
          style={{
            ...TEXT_STYLE,
            ...scrollbarPad,
            textAlign: isRtl ? "right" : "left"
          }}
        >
          {hasHighlight
            ? splitHighlightSegments(text).map((seg, i) =>
                seg.highlight ? (
                  <mark
                    key={i}
                    className="rounded-sm bg-[#A4C63966] text-transparent dark:bg-[#A4C63980]"
                  >
                    {seg.text}
                  </mark>
                ) : (
                  <span key={i}>{seg.text}</span>
                )
              )
            : null}
          {/* Trailing newline keeps the last line's height in sync. */}
          {"\n"}
        </div>

        <textarea
          id="template-instructions-field"
          ref={textareaRef}
          dir={dir}
          className={`${BOX_CLASS} relative block w-full min-h-[100px] resize-y border-gray-300 bg-transparent focus:outline-none focus:ring-2 focus:ring-green-400 dark:border-gray-600 dark:text-gray-200 dark:focus:ring-green-600`}
          value={text}
          onChange={e => onChange(e.target.value)}
          onScroll={syncLayout}
          placeholder={t("enterTemplateInstructions")}
          rows={4}
          style={{ ...TEXT_STYLE, textAlign: isRtl ? "right" : "left" }}
        />
      </div>
    </div>
  );
}
